import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Box,
  Divider,
  Spinner,
  DescriptionList,
  Button,
} from '@shopify/polaris';
import { api } from '../utils/api';
import { getStatusBadge, formatDate, formatDateTime, getCarrierName } from '../utils/helpers';

interface TrackingEvent {
  id: string;
  status: string;
  description: string;
  location?: string;
  occurredAt: string;
}

interface ShipmentDetail {
  id: string;
  trackingNumber: string;
  carrier: string;
  status: string;
  statusDetail?: string;
  orderNumber?: string;
  customerName?: string;
  customerEmail?: string;
  destinationCity?: string;
  destinationCountry?: string;
  carrierTrackingUrl?: string;
  eta?: string;
  deliveredAt?: string;
  createdAt: string;
  lastCheckedAt?: string;
  events?: TrackingEvent[];
}

export default function ShipmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: shipment, isLoading, error } = useQuery<ShipmentDetail>({
    queryKey: ['shipment', id],
    queryFn: () => api.get('/shipments/' + id),
  });

  const refreshMutation = useMutation({
    mutationFn: () => api.get('/shipments/' + id + '/refresh'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment', id] });
    },
  });

  if (isLoading) {
    return (
      <Page title="Chargement...">
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  if (error || !shipment) {
    return (
      <Page title="Erreur">
        <Banner tone="critical">
          Impossible de charger les détails de l'expédition
        </Banner>
      </Page>
    );
  }

  const events = shipment.events ?? [];

  return (
    <Page
      title={'Suivi ' + shipment.trackingNumber}
      backAction={{ content: 'Expéditions', onAction: () => navigate('/shipments') }}
      titleMetadata={getStatusBadge(shipment.status)}
      primaryAction={{
        content: 'Rafraîchir',
        loading: refreshMutation.isPending,
        onAction: () => refreshMutation.mutate(),
      }}
    >
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Historique de suivi
              </Text>
              <Divider />

              {events.length === 0 ? (
                <Text as="p" tone="subdued">
                  Aucun événement de suivi disponible
                </Text>
              ) : (
                <BlockStack gap="400">
                  {events.map((event, index) => (
                    <Box key={event.id} paddingBlockStart="200">
                      <InlineStack gap="400" align="start">
                        <Box
                          background={index === 0 ? 'bg-fill-success' : 'bg-fill-secondary'}
                          borderRadius="full"
                          minWidth="12px"
                          minHeight="12px"
                        />
                        <BlockStack gap="100">
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {event.description}
                          </Text>
                          <InlineStack gap="200">
                            {event.location ? (
                              <Text as="span" tone="subdued" variant="bodySm">
                                {event.location}
                              </Text>
                            ) : null}
                            <Text as="span" tone="subdued" variant="bodySm">
                              {formatDateTime(event.occurredAt)}
                            </Text>
                          </InlineStack>
                        </BlockStack>
                      </InlineStack>
                    </Box>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Informations
                </Text>
                <Divider />
                <DescriptionList
                  items={[
                    { term: 'Numéro de suivi', description: shipment.trackingNumber },
                    { term: 'Transporteur', description: getCarrierName(shipment.carrier) },
                    { term: 'Commande', description: shipment.orderNumber ?? '-' },
                    { term: "Date d'expédition", description: formatDate(shipment.createdAt) },
                  ]}
                />
              </BlockStack>
            </Card>

            {(shipment.customerName || shipment.customerEmail) ? (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Client
                  </Text>
                  <Divider />
                  <DescriptionList
                    items={[
                      ...(shipment.customerName ? [{ term: 'Nom', description: shipment.customerName }] : []),
                      ...(shipment.customerEmail ? [{ term: 'Email', description: shipment.customerEmail }] : []),
                      ...(shipment.destinationCity ? [{ term: 'Ville', description: shipment.destinationCity }] : []),
                    ]}
                  />
                </BlockStack>
              </Card>
            ) : null}

            {shipment.carrierTrackingUrl ? (
              <Button url={shipment.carrierTrackingUrl} external fullWidth>
                Voir sur {getCarrierName(shipment.carrier)}
              </Button>
            ) : null}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
